import { computed } from 'vue'
import {
  useTransactionAPIStore,
  useWalletStore,
  useCoinStore,
  useAccountTokenAPIStore,
  useAccountAPIStore,
  useAccountBalanceAPIStore,
  useCoinPriceAPIStore,
  useCurrencyAPIStore,
} from '@/stores'
import {
  type Maybe,
  type BaseAccount,
  type EncUVD,
  Wallet,
  AccountToken,
  Nft,
  Coin,
  Address,
  CoinAmount,
  type IPrepareData,
  type ISendExtraData,
  type ITransactionExecutor,
  EthereumCoinTransactionExecutor,
  SolanaCoinTransactionExecutor,
  NearCoinTransactionExecutor,
  AptosCoinTransactionExecutor,
} from '@/types'
import type { IAccount, ICurrency } from '@/types/api/wallet'

class Account implements BaseAccount {
  id: string
  walletId: string
  label: string
  currencyId: string
  currency: ICurrency
  balance: string
  used: boolean
  addressPath: string
  address: Address
  parentAccountId: string | null
  createdAt: string
  updatedAt: string
  wallet: Maybe<Wallet>
  tokens: Maybe<AccountToken[]>
  nfts: Maybe<Nft[]>
  coin: Maybe<Coin>
  amount!: CoinAmount
  percent?: string
  private _transactionExecutor: Maybe<ITransactionExecutor>

  constructor({ account }: { account: IAccount }) {
    this.id = account.id
    this.walletId = account.walletId
    this.label = account.label
    this.currencyId = account.currencyId
    this.currency = account.currency
    this.balance = account.balance
    this.used = account.used
    this.addressPath = account.addressPath
    this.address = new Address(account.addresses[0] || '')
    this.parentAccountId = account.parentAccountId
    this.createdAt = account.createdAt
    this.updatedAt = account.updatedAt

    const walletStore = useWalletStore()
    const computedWallet = computed(() => {
      return walletStore.getWallet(this.walletId)
    })
    Object.defineProperty(this, 'wallet', {
      get(): Maybe<Wallet> {
        return computedWallet.value
      },
    })
    const computedTokens = computed(() => {
      return walletStore.accountTokenList.filter((token) => {
        return token.parentAccountId === this.id
      })
    })
    Object.defineProperty(this, 'tokens', {
      get(): Maybe<AccountToken[]> {
        return computedTokens.value
      },
    })
    const computedNfts = computed(() => {
      return walletStore.nftList.filter((nft) => {
        return nft.accountId === this.id
      })
    })
    Object.defineProperty(this, 'nfts', {
      get(): Maybe<Nft[]> {
        return computedNfts.value
      },
    })
    const _coinStore = useCoinStore()
    const coinPriceAPIStore = useCoinPriceAPIStore()
    const currencyAPIStore = useCurrencyAPIStore()
    const computedCoin = computed(() => {
      // currency 객체로 Coin 정보 생성
      const coreNetwork =
        this.currencyId?.split('-')[0]?.split(':')[0] || this.currencyId || ''
      // Currency Store에서 전체 currency 정보 가져오기 (blockExplorerUrl 포함)
      const fullCurrency = currencyAPIStore.getCurrencyById(
        this.currencyId,
      ) as any
      const coinGeckoId = this.currency?.cgkId

      // 테스트넷 여부 확인
      const isTestnet =
        this.currencyId?.toLowerCase().includes('testnet') ||
        this.currencyId?.toLowerCase().includes('devnet')

      // CoinGecko ID로 가격 정보 조회 (테스트넷은 제외)
      const priceInfo =
        !isTestnet && coinGeckoId
          ? coinPriceAPIStore.coinPriceList.find((p) => p.id === coinGeckoId)
          : undefined

      // currencyId를 numeric ID로 변환 (grouping을 위해 필요)
      const stringToNumericId = (str: string): number => {
        let hash = 0
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i)
          hash = (hash << 5) - hash + char
          hash = hash & hash
        }
        return Math.abs(hash)
      }

      const numericId = stringToNumericId(this.currencyId || 'unknown')

      // currencyId 기반 색상 생성
      const generateColor = (str: string): string => {
        const colorFromApi = this.currency?.color
        if (colorFromApi && colorFromApi !== '#' && colorFromApi.length > 1) {
          return colorFromApi
        }

        let hash = 0
        for (let i = 0; i < str.length; i++) {
          hash = str.charCodeAt(i) + ((hash << 5) - hash)
        }

        const h = Math.abs(hash % 360)
        const s = 60
        const l = 35

        return `hsl(${h}, ${s}%, ${l}%)`
      }

      // V2 blockExplorerUrl -> V1 explorer 필드 변환
      // fullCurrency에서 blockExplorerUrl 가져오기 (Wallet API의 currency에는 없음)
      const blockExplorerUrl = fullCurrency?.blockExplorerUrl as any

      const coinObj = {
        id: numericId,
        name: this.currency?.name || '',
        symbol: this.currency?.ticker || '',
        network: this.currencyId,
        coreNetwork: coreNetwork.toUpperCase(),
        iconUrl: this.currency?.iconUrl || '',
        decimals: this.currency?.decimals || 18,
        coinGeckoId: coinGeckoId,
        isTestnet: isTestnet,
        feeType: 'GENERAL',
        coin: undefined,
        color: generateColor(this.currencyId || 'unknown'),
        priceInfo: priceInfo,
        // Network info for UI display
        networkIcon: this.currency?.iconUrl || '',
        networkName: this.currency?.name || this.currencyId || '',
        networkSymbol: this.currency?.ticker || '',
        networkDecimals: this.currency?.decimals || 18,
        // Explorer URLs (V2 blockExplorerUrl에서 변환)
        explorerUrl: blockExplorerUrl?.url,
        explorerAddress: blockExplorerUrl?.address?.replace(
          '$address',
          '{address}',
        ),
        explorerTransaction: blockExplorerUrl?.tx?.replace('$hash', '{txID}'),
        explorerContract: blockExplorerUrl?.contract?.replace(
          '$address',
          '{address}',
        ),
      } as any

      return coinObj
    })
    Object.defineProperty(this, 'coin', {
      get(): Maybe<Coin> {
        return computedCoin.value
      },
    })

    // amount를 computed로 정의 (accountBalanceList 변경 시 자동 갱신)
    const accountBalanceStore = useAccountBalanceAPIStore()
    const computedAmount = computed(() => {
      // accountBalanceList에 값이 있으면 우선 사용 (새로고침 후 업데이트된 값)
      const updatedBalance = accountBalanceStore.accountBalanceList[this.id]
      const currentBalance = updatedBalance ?? this.balance

      return new CoinAmount({
        amount: currentBalance,
        decimal: computedCoin.value?.decimals ?? 18,
      })
    })
    Object.defineProperty(this, 'amount', {
      get(): CoinAmount {
        return computedAmount.value
      },
    })
  }

  get transactionExecutor(): Maybe<ITransactionExecutor> {
    if (!this._transactionExecutor) {
      switch (this.coin?.coreNetwork) {
        case 'ETHEREUM':
        case 'KLAYTN':
          this._transactionExecutor = new EthereumCoinTransactionExecutor({
            account: this,
          })
          break
        case 'SOLANA':
          this._transactionExecutor = new SolanaCoinTransactionExecutor({
            account: this,
          })
          break
        case 'NEAR':
          this._transactionExecutor = new NearCoinTransactionExecutor({
            account: this,
          })
          break
        case 'APTOS':
          this._transactionExecutor = new AptosCoinTransactionExecutor({
            account: this,
          })
          break
      }
    }
    return this._transactionExecutor
  }

  public getKey(): string {
    return 'account' + this.id
  }

  async fetchBalance(force: boolean | undefined = false) {
    const accountBalanceStore = useAccountBalanceAPIStore()
    return accountBalanceStore.fetchAccountBalance({
      accountId: this.id,
      force,
    })
  }

  async fetchAccountTokens(force: boolean | undefined = false) {
    const accountTokenAPIStore = useAccountTokenAPIStore()
    return accountTokenAPIStore.fetchTokenList(
      {
        accountIds: this.id,
      },
      force,
    )
  }

  async setLabel(label: string) {
    const accountAPIStore = useAccountAPIStore()
    await accountAPIStore.updateAccount(this.id, { label })
  }

  async hideAccount(skipRefresh = false) {
    const accountAPIStore = useAccountAPIStore()
    await accountAPIStore.deleteAccount(this.id, skipRefresh)
  }

  async fetchTransactionList(
    force?: boolean,
    refresh?: boolean,
  ): Promise<void> {
    const txAPIStore = useTransactionAPIStore()
    await txAPIStore.fetchAccountTransactionList({
      accountId: this.id,
      force: force,
      refresh: refresh,
    })
  }

  public async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<IPrepareData> {
    if (!this.transactionExecutor) {
      throw new Error('transactionExecutor is not exist')
    }
    return this.transactionExecutor.getPrepareData({
      to: params.to,
      amount: params.amount,
    })
  }

  async send(params: {
    to: string
    amount: string
    extraData: ISendExtraData
    encUVD: EncUVD
    otpCode?: { code: string; isRecoveryCode?: boolean }
  }) {
    if (!this.transactionExecutor) {
      throw new Error('transactionExecutor is not exist')
    }
    const signResponse = await this.transactionExecutor.sign({
      to: params.to,
      amount: params.amount,
      encUVD: params.encUVD,
      extraData: params.extraData,
      otpCode: params.otpCode,
    })
    return this.transactionExecutor.broadcast({ signResponse })
  }
}

class AccountFactory {
  private static instance: AccountFactory
  private constructor() {}

  public static getInstance(): AccountFactory {
    if (!AccountFactory.instance) {
      AccountFactory.instance = new AccountFactory()
    }
    return AccountFactory.instance
  }

  makeAccount(account: IAccount) {
    const accountInstance = new Account({ account })
    return accountInstance
  }
}

export { Account, AccountFactory }
